import React, { useEffect, createContext, useState } from 'react';
import {
    OnboardingFinished,
    OnboardingMobile,
    InstitutionalOnboardingPersonal,
    InstitutionalOnboardingInstitution,
    InstitutionalOnboardingDirectors
} from 'pages/Onboarding'
import {
    Box,
    Heading,
    Progress,
    Text,
    Flex,
    Spinner,
    Image
} from 'theme-ui';
import { Icon } from 'assets/Icon';
import { Layout } from '~/Layouts';
import { LoadingScreen, Logo, BackButton, Button, Container } from '~/Common';
import { toast } from 'utils/toast';
import { onboardToken, onboardLogin, setUser } from 'features/auth/authSlice';
import { useDispatch, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getCsrfCookie } from 'utils/api';
import {InstitutionalOnboardingSteps} from "../../../app/constants";
import { useDocumentTitle } from 'hooks/useDocumentTitle';

const InstitutionalOnboardingContext = createContext();

const mapStateToProps = (state) => {
    return { user: state.user?.user, userStatus: state.user?.status, appTheme: state.theme }
}

const InstitutionalOnboardingLanding = ({ user, userStatus, appTheme, ...props }) => {
    const token = props.match.params.token;
    const [loginAttempted, setLoginAttempted] = useState(false);
    const [onboardingStepChecked, setOnboardingStepChecked] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    useDocumentTitle(t('Onboarding'));
    const [showInvite, setShowInvite] = useState(false);
    const [currentStep, setCurrentStep] = useState(InstitutionalOnboardingSteps.mobile);
    const [isLoading, setLoading] = useState(true);
    const [isReady, setReady] = useState(false);
    const [errors, setErrors] = useState([]);
    const [isSetup, setSetup] = useState(false);
    const [reverseSlides, setReverseSlides] = useState(false);

    const triggerError = (error, data = {}) => {
        setErrors([
            ...errors,
            {
                message: error,
                ...data
            }
        ]);
    };

    useEffect(() => {
        // If we have a logged in user, we don't need to login via token
        if(!loginAttempted && token){
            setLoading(true);
            setOnboardingStepChecked(false);

            // Get login via CSRF and token, have to wait for csrf to run first
            getCsrfCookie().then(() => {
                onboardLogin({
                    onboarding_token: token,
                    device_name: 'App'
                }).then((response) => {
                    dispatch(setUser(response));

                    if (history.location.pathname !== '/institution/onboarding') {
                        history.push('/institution/onboarding');
                    }

                    setLoading(false);
                }).catch(({ response }) => {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('authTokenScope');
                    toast.error(response?.data?.message || t('login.error'), {
                        toastId: 'login-error',
                    });
                    history.push('/');
                });
                setLoginAttempted(true);
            })
        } else {
            setLoading(false);
        }
    }, [token, dispatch, t, history, loginAttempted]);

    useEffect(() => {
        if(!onboardingStepChecked && userStatus === 'idle'){
            setOnboardingStepChecked(true);
        }

        if (user?.onboarding) {
            if(JSON.parse(localStorage.getItem('onboardingFirstLoaded')) !== true){
                setShowInvite(user?.tasks?.tests?.mobile_verified === false);
            }

            setReady(true);
        }
    }, [token, user, setShowInvite, showInvite, userStatus, onboardingStepChecked]);

    useEffect(() => {
        if (user?.finished_onboarding_details) {
            setLoading(true);
            onboardToken({
                device_name: 'App'
            }).then((response) => {
                dispatch(setUser(response));
                history.push('/institution/dashboard');
            });
        }
    }, [user, dispatch, history, currentStep]);

    const closeModal = (e) => {
        e.preventDefault();
        localStorage.setItem('onboardingFirstLoaded', true);
        setShowInvite(false);
    };

    const canGoBack = step => {
        switch (currentStep) {
            case 3:
            case 4:
                return true;
            default:
                return false;
        }
    }

    const goBack = (e, force) => {
        if (e) {
            e.preventDefault();
        }

        const allow = canGoBack(currentStep) || force;
        var nextIndex = allow ? currentStep - 1 : currentStep;

        setCurrentStep(nextIndex);
    };

    const nextStep = (e) => {
        if (e) {
            e.preventDefault();
        }

        setCurrentStep(currentStep + 1);
    }

    useEffect(() => {
        if (!isSetup && user?.onboarding) {
            setSetup(true);

            let passed = true;

            if(user?.tasks?.tests?.has_directors_details === false){
                setCurrentStep( InstitutionalOnboardingSteps.directors );
                passed = false;
            }
            if(user?.tasks?.tests?.has_institutional_details === false){
                setCurrentStep( InstitutionalOnboardingSteps.institution );
                passed = false;
            }
            if(user?.tasks?.tests?.has_personal_details === false){
                setCurrentStep( InstitutionalOnboardingSteps.personal );
                passed = false;
            }
            if(user?.tasks?.tests?.mobile_verified === false){
                setCurrentStep( InstitutionalOnboardingSteps.mobile );
                passed = false;
            }
            if(passed && isLoading) {
                setCurrentStep( InstitutionalOnboardingSteps.finished );
            }
        }

    }, [user, isLoading, isSetup, setSetup]);

    if (!isReady) {
        return <LoadingScreen />;
    }

    const renderStep = () => {
        if (isLoading) {
            return (
                <Flex sx={{ justifyContent: 'center' }}>
                    <Spinner variant="styles.spinner" />
                </Flex>
            );
        }

        // TODO: here
        switch(currentStep){
            case InstitutionalOnboardingSteps.mobile: return <OnboardingMobile />;
            case InstitutionalOnboardingSteps.personal: return <InstitutionalOnboardingPersonal />;
            case InstitutionalOnboardingSteps.institution: return <InstitutionalOnboardingInstitution />;
            case InstitutionalOnboardingSteps.directors: return <InstitutionalOnboardingDirectors />;
            case InstitutionalOnboardingSteps.finished: return <OnboardingFinished />;

            default:
                return <Text as="p">Unable to find step</Text>;
        }
    };

    if (showInvite) {
        return (
            <Flex sx={{
                background: '#3F6D6A',
                backgroundSize: 'cover',
                backgroundPosition: 'top center',
                minHeight: '100vh',
                alignItems: 'center',
                textAlign: 'center'
            }}>
                <Image src="/img/invite-banner.png" sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }} />
                <Container size="612px" sx={{ zIndex: 10 }}>
                    <Box sx={{
                        overflow: 'auto',
                        minHeight: ['100vh', 0],
                        pt: 5,
                        position: 'relative',
                        zIndex: 3
                    }}>
                        <Icon icon="mail-open" size="53px" color="white" sx={{ mb: '20px' }} />
                        <Heading
                            as="h2"
                            mb={3}
                            id="onboardingTitle"
                            color="white"
                            sx={{
                                fontSize: ['48px', '48px', '64px'],
                                fontWeight: 400
                            }}
                        >{t('onboarding.institutional.landing.invite.title')}</Heading>
                        <Heading
                            as="h3"
                            variant="h4"
                            color="white"
                            sx={{
                                fontSize: '26px',
                                maxWidth: '516px',
                                fontWeight: 500,
                                textAlign: 'center',
                                margin: '0 auto',
                                mt: 5
                            }}
                        >{t('onboarding.institutional.landing.invite.subtitle')}</Heading>

                        <Button variant="primary" id="acceptInvite" sx={{
                            width: '100%',
                            maxWidth: '280px',
                            mt: [4,'53px']
                        }} type="button" onClick={closeModal}>{t('onboarding.institutional.landing.invite.accept')}</Button>
                    </Box>

                    <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 2,
                        px: 2,
                    }}>
                        <Logo p={4} px="40px" width="180" color="white" center={true} />
                        <Text as="p" mb={4} color="white" sx={{
                            fontSize: '14px'
                        }}>{t('onboarding.institutional.landing.invite.description')}</Text>
                    </Box>
                </Container>
            </Flex>
        )
    }

    return (
        <Layout>
            <InstitutionalOnboardingContext.Provider value={{
                currentStep,
                setCurrentStep,
                isLoading,
                setLoading,
                errors,
                triggerError,
                nextStep,
                goBack,
                reverseSlides,
                setReverseSlides
            }}>
                {canGoBack(currentStep) && (
                    <BackButton onClick={goBack} sx={{
                        position: 'absolute',
                        top: [4,5],
                        left: [2,0],
                        mt: '-10px'
                    }} />
                )}
                <Container>
                    <Progress max={1} value={currentStep / 9} mb={5} >
                       {parseFloat(currentStep / 9)}%
                    </Progress>

                    {renderStep()}
                </Container>
            </InstitutionalOnboardingContext.Provider>
        </Layout>
    );
};

export { InstitutionalOnboardingContext };
export default connect(mapStateToProps, null)(InstitutionalOnboardingLanding);
